import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Flag from './Flag.jsx'
import { SUPPORTED_LANGUAGES } from './i18n'

/** Whether the visitor has already answered, in this browser. */
export function languageChosen() {
  return Boolean(localStorage.getItem('doseg.lang'))
}

/**
 * The language a visitor's browser says they read, when it is one we speak and not the default.
 * From the browser rather than the network address: an IP says where a person is standing, not
 * what they read, and asks a question about them nobody needs answered.
 */
export function suggestedLanguage() {
  const spoken = (navigator.languages ?? [navigator.language]).map((tag) => tag.toLowerCase().split('-')[0])
  const fromLanguage = spoken.find((code) => SUPPORTED_LANGUAGES.includes(code) && code !== 'en')

  if (fromLanguage) return fromLanguage

  // A phone set to English in Skopje is the ordinary case, not the exception. The time zone says
  // where the person is standing, without asking anyone.
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  const byZone = { 'Europe/Skopje': 'mk', 'Europe/Tirane': 'sq', 'Europe/Istanbul': 'tr' }

  return byZone[zone] ?? null
}

/**
 * A one-time offer to switch languages.
 *
 * English is the default everywhere. When the browser says its owner reads Macedonian, Albanian
 * or Turkish, this appears once — the four languages as flags, in the suggested language's own
 * words — and whatever they tap is remembered. Dismissing it counts as choosing English, so it
 * never asks twice.
 */
export default function LanguageOffer() {
  const { t, i18n } = useTranslation()
  const [suggested, setSuggested] = useState(null)

  useEffect(() => {
    if (!languageChosen()) setSuggested(suggestedLanguage())
  }, [])

  if (!suggested) return null

  const choose = (code) => {
    i18n.changeLanguage(code)
    localStorage.setItem('doseg.lang', code)
    setSuggested(null)
  }

  return (
    <div className="langoffer" role="dialog" aria-label={t('language.offerTitle', { lng: suggested })}>
      <p className="langoffer__title">{t('language.offerTitle', { lng: suggested })}</p>
      <div className="langoffer__flags">
        {SUPPORTED_LANGUAGES.map((code) => (
          <button
            key={code}
            type="button"
            className={`langoffer__flag${code === suggested ? ' is-suggested' : ''}`}
            onClick={() => choose(code)}
          >
            <Flag code={code} />
            <span>{t(`language.${code}`)}</span>
          </button>
        ))}
      </div>
      <button type="button" className="langoffer__dismiss" onClick={() => choose('en')}>
        {t('language.keepEnglish', { lng: suggested })}
      </button>
    </div>
  )
}
